import React, { useId, useState } from 'react';
import { BookOpen, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';

/**
 * "What we know now": the research behind an evening, closed until the reader
 * opens it. The guides cannot know any of this, so it never sits in their
 * mouths; and stacking it between scenes stopped the story for a lecture. Here
 * it waits at the end for whoever wants it. Each note names its study.
 *
 * Block shape (src/data/modules/*.js):
 *   { type: 'notes', title, intro, items: [{ title, paragraphs: [], source }] }
 */
const ScienceNotes = ({ title = 'What we know now', intro, items = [] }) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  if (items.length === 0) return null;

  return (
    <div className="my-8 rounded-2xl border border-courtyard-aegean/30 bg-courtyard-aegean/5 overflow-hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 text-left p-5 hover:bg-courtyard-aegean/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-courtyard-aegean"
      >
        <span className="w-9 h-9 rounded-lg bg-courtyard-aegean/20 flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-5 h-5 text-courtyard-aegean-dark" aria-hidden="true" />
        </span>
        <span className="flex-1">
          <span className="block font-display font-bold text-courtyard-stone">{title}</span>
          <span className="block text-sm text-courtyard-stone/80">
            {items.length} {items.length === 1 ? 'note' : 'notes'}: what has been tested, and what is only our own view
          </span>
        </span>
        <ChevronDown
          className={clsx('w-5 h-5 text-courtyard-stone/80 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      <div id={panelId} hidden={!open} className="px-5 pt-4 pb-6">
        {intro && (
          <p className="text-[15px] text-courtyard-stone/80 leading-relaxed mb-5">{intro}</p>
        )}
        <div className="space-y-6">
          {items.map((item) => (
            <section key={item.title}>
              <h4 className="font-display font-bold text-courtyard-stone">{item.title}</h4>
              <div className="mt-1.5 space-y-2 text-[15px] text-courtyard-stone/90 leading-relaxed">
                {(item.paragraphs || []).map((p) => <p key={p}>{p}</p>)}
              </div>
              {item.source && (
                <p className="mt-2 text-sm text-courtyard-aegean-dark font-medium">{item.source}</p>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScienceNotes;
